import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Target, AlertTriangle, CheckCircle, Clock, ChevronRight, 
  FileX, FileCheck, RefreshCw, GitCompare
} from 'lucide-react';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';

/**
 * ScopeHealthWidget - Executive Cockpit tile for scope health across portfolio
 * Shows: Projects without baseline, Candidates pending, Recently updated
 */
const ScopeHealthWidget = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    no_baseline: [],
    pending_candidates: [],
    recently_updated: [],
    total_projects: 0,
    baselined_projects: 0
  });
  const [filter, setFilter] = useState('all'); // 'all' | 'no_baseline' | 'pending' | 'updated'
  const [error, setError] = useState(null);

  const tenantId = user?.tenant?.id || user?.tenant_id;

  useEffect(() => {
    if (tenantId) {
      loadScopeHealth();
    } else {
      setLoading(false);
    }
  }, [tenantId]);

  const loadScopeHealth = async () => {
    if (!tenantId) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      const res = await api.getScopeHealth(tenantId);
      setData(res.data);
    } catch (err) {
      console.error('Failed to load scope health:', err);
      setError('Unable to load scope health data');
      // Keep default fallback data
    } finally {
      setLoading(false);
    }
  };

  const handleProjectClick = (project, viewType) => {
    if (viewType === 'pending') {
      // Go to baselines compare view
      navigate(`/scope-baselines?project=${project.id}&view=compare`);
    } else {
      // Go to scope workspace
      navigate(`/scope-workspace?project=${project.id}`);
    }
  };

  const getFilteredProjects = () => {
    if (!data) return [];
    
    switch (filter) {
      case 'no_baseline':
        return data.no_baseline || [];
      case 'pending':
        return data.pending_candidates || [];
      case 'updated':
        return data.recently_updated || [];
      default:
        return [];
    }
  };

  const filteredProjects = getFilteredProjects();

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-gray-100 rounded-lg animate-pulse" />
          <div className="h-6 w-32 bg-gray-100 rounded animate-pulse" />
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-12 bg-gray-100 rounded-lg animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  const noBaselineCount = data.no_baseline?.length || 0;
  const pendingCount = data.pending_candidates?.length || 0;
  const updatedCount = data.recently_updated?.length || 0;

  // Determine overall health status
  const getOverallHealth = () => {
    if (noBaselineCount > 5 || pendingCount > 5) return 'at-risk';
    if (noBaselineCount > 0 || pendingCount > 0) return 'needs-attention';
    return 'healthy';
  };

  const healthStatus = getOverallHealth();
  const healthColors = {
    'healthy': 'bg-green-500',
    'needs-attention': 'bg-amber-500',
    'at-risk': 'bg-red-500'
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="scope-health-widget">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-blue-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white rounded-lg shadow-sm flex items-center justify-center">
              <Target className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Scope Health</h3>
              <p className="text-xs text-gray-500">
                {data.baselined_projects || 0} of {data.total_projects || 0} projects baselined
              </p>
            </div>
          </div>
          
          {/* Health indicator */}
          <div className={`w-3 h-3 rounded-full ${healthColors[healthStatus]}`} 
               title={healthStatus === 'healthy' ? 'All projects healthy' : 
                      healthStatus === 'needs-attention' ? 'Some projects need attention' : 
                      'Multiple projects at risk'} />
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-3 border-b border-gray-200">
        <button
          onClick={() => setFilter(filter === 'no_baseline' ? 'all' : 'no_baseline')}
          className={`p-3 text-center transition-colors ${
            filter === 'no_baseline' ? 'bg-red-50' : 'hover:bg-gray-50'
          }`}
          data-testid="no-baseline-filter"
        >
          <div className={`text-lg font-bold ${noBaselineCount > 0 ? 'text-red-600' : 'text-gray-400'}`}>
            {noBaselineCount}
          </div>
          <div className="text-xs text-gray-500">No Baseline</div>
        </button>
        
        <button
          onClick={() => setFilter(filter === 'pending' ? 'all' : 'pending')}
          className={`p-3 text-center border-x border-gray-200 transition-colors ${
            filter === 'pending' ? 'bg-amber-50' : 'hover:bg-gray-50'
          }`}
          data-testid="pending-filter"
        >
          <div className={`text-lg font-bold ${pendingCount > 0 ? 'text-amber-600' : 'text-gray-400'}`}>
            {pendingCount}
          </div>
          <div className="text-xs text-gray-500">Pending</div>
        </button>
        
        <button
          onClick={() => setFilter(filter === 'updated' ? 'all' : 'updated')}
          className={`p-3 text-center transition-colors ${
            filter === 'updated' ? 'bg-blue-50' : 'hover:bg-gray-50'
          }`}
          data-testid="updated-filter"
        >
          <div className={`text-lg font-bold ${updatedCount > 0 ? 'text-blue-600' : 'text-gray-400'}`}>
            {updatedCount}
          </div>
          <div className="text-xs text-gray-500">Updated (7d)</div>
        </button>
      </div>

      {/* Project list */}
      <div className="max-h-48 overflow-y-auto">
        {filter === 'all' ? (
          <div className="p-4 text-center text-sm text-gray-500">
            <CheckCircle className="w-8 h-8 mx-auto mb-2 text-gray-300" />
            <p>Click a status above to see projects</p>
          </div>
        ) : filteredProjects.length === 0 ? (
          <div className="p-4 text-center text-sm text-gray-500">
            <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-400" />
            <p>No projects in this category</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {filteredProjects.slice(0, 5).map((project, idx) => (
              <button
                key={project.id || idx}
                onClick={() => handleProjectClick(project, filter)}
                className="w-full p-3 flex items-center justify-between hover:bg-gray-50 transition-colors text-left"
                data-testid={`project-row-${idx}`}
              >
                <div className="flex items-center gap-3">
                  {filter === 'no_baseline' && <FileX className="w-4 h-4 text-red-500" />}
                  {filter === 'pending' && <Clock className="w-4 h-4 text-amber-500" />}
                  {filter === 'updated' && <FileCheck className="w-4 h-4 text-blue-500" />}
                  <div>
                    <div className="font-medium text-sm text-gray-900">{project.name}</div>
                    <div className="text-xs text-gray-500">
                      {filter === 'no_baseline' && 'No approved baseline'}
                      {filter === 'pending' && `Candidate V${project.candidate_version || '?'} awaiting approval`}
                      {filter === 'updated' && `V${project.latest_version || '?'} approved ${project.updated_ago || 'recently'}`}
                    </div>
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-400" />
              </button>
            ))}
            
            {filteredProjects.length > 5 && (
              <button
                onClick={() => navigate('/scope-baselines')}
                className="w-full p-3 text-center text-sm text-[var(--clr-brand-primary)] hover:bg-gray-50"
              >
                View all {filteredProjects.length} projects
              </button>
            )}
          </div>
        )}
      </div>

      {/* Footer link */}
      <div className="p-3 border-t border-gray-200 bg-gray-50">
        <button
          onClick={() => navigate('/scope-baselines')}
          className="w-full flex items-center justify-center gap-2 text-sm text-gray-600 hover:text-[var(--clr-brand-primary)] transition-colors"
          data-testid="view-all-baselines-btn"
        >
          <GitCompare className="w-4 h-4" />
          View All Baselines
        </button>
      </div>
    </div>
  );
};

export default ScopeHealthWidget;
